// src/components/suiviEnseignants/PageEnseignantSuivi.jsx
// ============================================================================
// Suivi des enseignants — la page d'un enseignant
// (/administration/enseignants/:employeId)
// ============================================================================
//
// Page dédiée, atteinte depuis la liste (navigation drill-in) : ses
// programmes, ses appels de la semaine, ses avis. La direction y commente un
// programme si elle en a le droit (`droits.commenter`,
// SUIVI_ENSEIGNANTS.VUE_GLOBALE.COMMENTER) ; l'onglet « Avis » n'existe que si
// elle peut lire les avis (sinon le serveur rend `avis: null`).
//
// L'onglet « Notes » montre les saisies de notes que l'enseignant a faites
// depuis son espace, chacune menant à sa page de validation — la même que
// depuis le carnet (validationNotes/) : la feuille au collège et au lycée, le
// mois de la classe au primaire (N3). Il lit /api/validation-notes et
// n'existe que si l'école a ouvert l'espace enseignant et le carnet, et que la
// personne lit au moins un niveau (NOTES.VALIDATION.* ou
// NOTES.VALIDATION_PRIMAIRE.*, sans repli de l'un sur l'autre) : la porte du
// suivi, que le groupe Enseignant détient, n'y suffit pas. Il ne décide rien.
//
// Consignes de Daniel (maquette N3, 7 octobre 2026) : le retour est un bouton
// bordé, le titre de la page en gras prononcé, les badges à l'arrondi d'un
// bouton, le tiret court dans les textes.
//
// L'onglet et la semaine vivent dans l'adresse (`?onglet=…&semaine=…`) : un
// rechargement ou un lien partagé ramène à la même vue.
//
// Les chiffres de l'en-tête sont ceux du `resume` du serveur
// (SuiviEnseignantsService.ficheEnseignant), calculés avec les règles de la
// liste : recompter ici les créneaux ou les avis reçus donnait d'autres
// nombres que la liste (un appel du matin fait un samedi est montré, pas
// compté ; la page ne reçoit que les 200 avis les plus récents).
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, CalendarCheck, ClipboardList, GraduationCap, Inbox, MessageSquare } from 'lucide-react';
import EtatVide from '../ui/EtatVide';
import LoadingSpinner from '../LoadingSpinner';
import { Toast } from '../ui/Toast';
import LongToast, { shouldUseLongToast } from '../ui/LongToast';
import ToastPortal from '../ToastPortal';
import { useFeatures } from '../../contexts/FeaturesContext';
import { usePermissions } from '../../hooks/usePermissions';
import { LigneAvis } from '../avisEnseignants/presentationAvis';
import { suiviEnseignants } from './apiSuiviEnseignants';
import { CarteCompteur, ChoixSemaine, ETATS_ESPACE, phraseEspace } from './elementsSuivi';
import { EST_DATE, enPourcentage, initiales, libelleDe, nombre, nomPersonne, periode, pluriel } from './formatSuivi';
import ProgrammesEnseignant from './ProgrammesEnseignant';
import AppelsEnseignant from './AppelsEnseignant';
import NotesEnseignantSuivi from '../validationNotes/NotesEnseignantSuivi';
import { useAccesValidationNotes } from '../validationNotes/droitsValidationNotes';
import { BoutonRetour } from '../validationNotes/presentationNotes';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const listeDe = (valeur) => (Array.isArray(valeur) ? valeur.map(libelleDe).filter(Boolean) : []);

// Une valeur du résumé du serveur, ou null si elle manque : l'écran écrit
// alors « - » plutôt qu'un zéro qui aurait l'air d'un chiffre.
const chiffre = (valeur) => (valeur === null || valeur === undefined ? null : nombre(valeur));

// Une icone par onglet, comme partout ailleurs : c'est elle qu'on reconnait
// avant de lire le libelle.
const ONGLETS = [
  { cle: 'programme', libelle: 'Programme', icone: ClipboardList },
  { cle: 'appels', libelle: 'Appels', icone: CalendarCheck },
  { cle: 'avis', libelle: 'Avis', icone: MessageSquare },
  { cle: 'notes', libelle: 'Notes', icone: GraduationCap }
];

const PageEnseignantSuivi = () => {
  const { employeId } = useParams();
  const [parametres, setParametres] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { estActive } = useFeatures();
  const { hasPermission } = usePermissions();
  const { voir: notesVisibles } = useAccesValidationNotes();

  const demandee = parametres.get('semaine');
  const semaine = EST_DATE.test(demandee || '') ? demandee : null;

  const [donnees, setDonnees] = useState(null);
  // `{ message, statut }` : le statut dit si « Réessayer » a un sens (pas pour
  // un enseignant introuvable ni une semaine refusée).
  const [erreur, setErreur] = useState(null);
  const [toast, setToast] = useState(null);

  // Seule la réponse de la DERNIÈRE demande s'affiche (changement rapide de
  // semaine, ou relecture après un commentaire).
  const demande = useRef(0);

  const charger = useCallback(async () => {
    const numero = ++demande.current;
    setErreur(null);
    try {
      const reponse = await suiviEnseignants.enseignant(employeId, semaine);
      if (numero === demande.current) setDonnees(reponse.data);
    } catch (e) {
      if (numero === demande.current) setErreur({ message: e.message, statut: e.statut || null });
    }
  }, [employeId, semaine]);

  // Un autre enseignant : on repart de zéro, sans afficher un instant le précédent.
  useEffect(() => { setDonnees(null); }, [employeId]);
  useEffect(() => { charger(); }, [charger]);

  // `state` repris tel quel : changer d'onglet ou de semaine remplace
  // l'adresse, et l'état de navigation perdu au passage (`depuisListe`)
  // faisait du lien de retour un nouvel aller vers une liste sans son filtre.
  const changerParametre = (cle, valeur) => {
    const suivants = new URLSearchParams(parametres);
    if (valeur) suivants.set(cle, valeur);
    else suivants.delete(cle);
    setParametres(suivants, { replace: true, state: location.state });
  };

  // Retour : à la liste telle qu'on l'a laissée (semaine, filtre, recherche,
  // tous dans son adresse) si l'on en vient ; sinon à la liste, à la semaine
  // affichée ici.
  const adresseListe = `/administration/enseignants${semaine ? `?semaine=${semaine}` : ''}`;
  const revenir = (e) => {
    if (location.state?.depuisListe) {
      e.preventDefault();
      navigate(-1);
    }
  };

  const commenter = async (programme, texte) => {
    let reponse;
    try {
      reponse = await suiviEnseignants.commenter(programme.programme_id, texte);
    } catch (e) {
      // Droit de commenter retiré, programme disparu : la page est en retard
      // sur la base, on la relit (le bouton « Commenter » disparaît derrière la
      // fenêtre). L'erreur remonte à la fenêtre, qui la montre et garde le texte.
      if (e?.statut === 403 || e?.statut === 404) charger();
      throw e;
    }
    setToast({ message: reponse?.message || "Commentaire publié. L'enseignant le lira sous son programme.", type: 'success' });
    // Le serveur rend les commentaires du programme : on les pose en place,
    // sans relire toute la page. Une forme inattendue fait relire.
    const commentaires = Array.isArray(reponse?.data) ? reponse.data : reponse?.data?.commentaires;
    if (Array.isArray(commentaires)) {
      setDonnees((d) => (d ? {
        ...d,
        programmes: d.programmes.map((p) => (p.programme_id === programme.programme_id ? { ...p, commentaires } : p))
      } : d));
    } else {
      await charger();
    }
  };

  const supprimerCommentaire = async (commentaire) => {
    try {
      const reponse = await suiviEnseignants.supprimerCommentaire(commentaire.id);
      setToast({ message: reponse?.message || 'Commentaire supprimé.', type: 'success' });
    } catch (e) {
      setToast({ message: e.message, type: 'error' });
    }
    // Dans les deux cas : un refus (commentaire déjà supprimé, pas le sien)
    // dit que l'écran est en retard sur la base.
    await charger();
  };

  if (!donnees) {
    return (
      <div className="w-full p-6">
        <BoutonRetour to={adresseListe} onClick={revenir}>Suivi des enseignants</BoutonRetour>
        {erreur ? (
          <div role="alert" className="mt-5 flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1">{erreur.message}</span>
            {/* Une semaine refusée (30 février dans l'adresse) : la redemander
                n'y changerait rien, on propose la semaine en cours. */}
            {semaine && erreur.statut === 400 ? (
              <button type="button" onClick={() => changerParametre('semaine', null)} className="font-semibold underline">
                Revenir à la semaine en cours
              </button>
            ) : erreur.statut !== 404 && (
              <button type="button" onClick={charger} className="font-semibold underline">Réessayer</button>
            )}
          </div>
        ) : (
          <LoadingSpinner message="Chargement de la page de l'enseignant…" size="lg" />
        )}
      </div>
    );
  }

  const enseignant = donnees.enseignant || {};
  const droits = donnees.droits || {};
  const avisVisibles = Array.isArray(donnees.avis);
  const onglets = ONGLETS.filter((o) => (o.cle !== 'avis' || avisVisibles) && (o.cle !== 'notes' || notesVisibles));
  const ongletDemande = parametres.get('onglet');
  const onglet = onglets.some((o) => o.cle === ongletDemande) ? ongletDemande : 'programme';
  const etatEspace = enseignant.espace?.etat || 'aucun';
  const [, teinteEspace] = ETATS_ESPACE[etatEspace] || ETATS_ESPACE.aucun;
  const matieres = listeDe(enseignant.matieres);
  const classes = listeDe(enseignant.classes);
  const peutOuvrirAvis = hasPermission('ELEVES') && estActive('module.vie_scolaire') && estActive('module.portail_enseignant');
  const semaineAppels = donnees.appels?.debut ? { debut: donnees.appels.debut, fin: donnees.appels.fin } : null;

  // Les chiffres de l'en-tête : ceux du serveur, tels quels.
  const resume = donnees.resume || {};
  const moyenne = chiffre(resume.pourcentage_moyen);
  const couples = nombre(resume.couples);
  const sansProgramme = nombre(resume.sans_programme);
  const enRetard = nombre(resume.programmes_en_retard);
  const appelsFaits = nombre(resume.appels_faits);
  const appelsAttendus = nombre(resume.appels_attendus);
  const appelsManquants = nombre(resume.appels_manquants);
  const avisMois = avisVisibles ? chiffre(resume.avis_mois) : null;
  const avisAnnee = avisVisibles ? chiffre(resume.avis_annee) : null;
  // La page ne reçoit que les avis les plus récents (200) : elle le dit.
  const avisTronques = avisVisibles && avisAnnee !== null && avisAnnee > donnees.avis.length;

  const choisirOnglet = (cle) => changerParametre('onglet', cle === 'programme' ? null : cle);

  return (
    <div className="w-full space-y-4 p-6">
      <BoutonRetour to={adresseListe} onClick={revenir}>Suivi des enseignants</BoutonRetour>

      {erreur && (
        <div role="alert" className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">La page n'a pas pu être rafraîchie : {erreur.message}</span>
          {semaine && erreur.statut === 400 ? (
            <button type="button" onClick={() => changerParametre('semaine', null)} className="font-semibold underline">
              Revenir à la semaine en cours
            </button>
          ) : (
            <button type="button" onClick={charger} className="font-semibold underline">Réessayer</button>
          )}
        </div>
      )}

      <section className="flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-white p-4">
        <span aria-hidden="true" className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#246990] text-lg font-semibold text-white">
          {initiales(enseignant)}
        </span>
        <div className="min-w-[240px] flex-1">
          <h1 className="text-2xl font-bold leading-tight text-[#246990]">{nomPersonne(enseignant) || 'Enseignant'}</h1>
          <p className="mt-0.5 text-[13px] text-gray-500">
            {[matieres.join(', '), classes.join(', ')].filter(Boolean).join(' · ') || "Aucune affectation cette année"}
          </p>
        </div>
        <div className="flex flex-col items-start gap-1 sm:items-end">
          <span className={`inline-flex h-6 items-center whitespace-nowrap rounded-lg border px-3 text-xs font-medium ${teinteEspace}`}>
            {phraseEspace(enseignant.espace)}
          </span>
          {/* L'accès lui-même (codes, suspension) se règle dans le dossier de
              l'employé, qui ne s'ouvre pas encore par une adresse : on dit où. */}
          <p className="text-[11px] text-gray-500">
            Accès réglé dans Ressources humaines › dossier de l'employé › « Accès enseignant »
          </p>
        </div>
      </section>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <CarteCompteur
          libelle="Avancement moyen"
          icone={ClipboardList}
          couleur="from-[#246990] to-[#3084b5]"
          valeur={moyenne === null ? '-' : enPourcentage(moyenne)}
          note={couples
            ? `${pluriel(couples - sansProgramme, 'programme commencé', 'programmes commencés')} sur ${couples}`
            : 'Aucune classe-matière'}
        />
        <CarteCompteur
          libelle="Programmes en retard"
          icone={ClipboardList}
          couleur="from-[#319190] to-[#63b5b4]"
          valeur={enRetard}
          sur={couples}
          ton={enRetard > 0 ? 'attention' : 'neutre'}
          note={sansProgramme > 0
            ? `${pluriel(sansProgramme, 'classe-matière', 'classes-matières')} sans programme saisi`
            : null}
        />
        {/* La semaine est écrite dans le libellé : hors de l'onglet Appels, rien
            d'autre ne dit de quelle semaine parle ce compte. */}
        <CarteCompteur
          libelle={semaineAppels ? `Appels ${periode(semaineAppels.debut, semaineAppels.fin)}` : 'Appels de la semaine'}
          icone={CalendarCheck}
          couleur="from-[#c66d59] to-[#ae7467]"
          valeur={appelsFaits}
          sur={appelsAttendus}
          ton={appelsManquants > 0 ? 'attention' : (appelsAttendus > 0 ? 'bien' : 'neutre')}
          note={appelsManquants > 0
            ? pluriel(appelsManquants, 'appel manquant', 'appels manquants')
            : (appelsAttendus > 0 ? 'Aucun appel manquant' : 'Aucun cours passé')}
        />
        {avisMois !== null && (
          <CarteCompteur
            libelle="Avis donnés ce mois"
            icone={MessageSquare}
            couleur="from-gray-800 to-gray-700"
            valeur={avisMois}
            note={avisAnnee !== null ? `${pluriel(avisAnnee, 'avis', 'avis')} depuis la rentrée` : null}
          />
        )}
      </div>

      {/* Des sous-onglets dans un panneau : la forme de `SubTabBar`
          (InterSiteManagement.jsx) — pastille grise compacte, largeur selon le
          contenu — et non celle des onglets de page, qui prendrait toute la
          largeur et chasserait le choix de la semaine de cette ligne.
          C'etait la DERNIERE barre d'onglets faite main du projet : Radix
          fournit le clavier (fleches, Debut, Fin) et les roles ARIA que ces
          vingt-huit lignes reecrivaient. */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <Tabs value={onglet} onValueChange={choisirOnglet}>
        <div className="flex flex-col gap-2 border-b border-gray-200 px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
          <TabsList
            aria-label="Suivi de l'enseignant"
            className="h-10 w-fit max-w-full gap-1 overflow-x-auto rounded-lg bg-gray-100 p-1"
          >
            {onglets.map((o) => (
              <TabsTrigger
                key={o.cle}
                value={o.cle}
                className="flex h-8 items-center gap-2 whitespace-nowrap rounded-md px-4 text-xs font-semibold text-gray-500 transition hover:text-gray-700 data-[state=active]:bg-[#246990] data-[state=active]:text-white data-[state=active]:shadow-sm"
              >
                <o.icone size={13} />
                {o.libelle}
              </TabsTrigger>
            ))}
          </TabsList>
          {onglet === 'appels' && (
            <div className="pb-2 sm:pb-0">
              <ChoixSemaine
                semaine={semaineAppels}
                choisie={semaine}
                onChoisir={(d) => changerParametre('semaine', d)}
              />
            </div>
          )}
        </div>

        <TabsContent value="programme" className="mt-0 p-4">
          <ProgrammesEnseignant
            programmes={donnees.programmes}
            peutCommenter={Boolean(droits.commenter)}
            onCommenter={commenter}
            onSupprimer={supprimerCommentaire}
          />
        </TabsContent>

        <TabsContent value="appels" className="mt-0 p-4">
          <AppelsEnseignant appels={donnees.appels} parentsOuverts={estActive('module.parents')} />
        </TabsContent>

        {notesVisibles && (
          <TabsContent value="notes" className="mt-0 p-4">
            <NotesEnseignantSuivi employeId={employeId} />
          </TabsContent>
        )}

        {avisVisibles && (
          <TabsContent value="avis" className="mt-0 p-4">
            {(
            donnees.avis.length === 0 ? (
              <EtatVide compact icone={Inbox} titre="Aucun avis" description="Cet enseignant n'a encore donné aucun avis sur ses élèves." />
            ) : (
              <>
                {avisTronques && (
                  <p className="mb-1 text-[11px] text-gray-500">
                    Les {donnees.avis.length} plus récents sur {avisAnnee} avis donnés depuis la rentrée.
                  </p>
                )}
                <ul className="divide-y divide-gray-100">
                  {donnees.avis.map((a) => <LigneAvis key={a.id} avis={a} masquerAuteur />)}
                </ul>
                <p className="mt-3 rounded-lg border border-[#246990]/20 bg-[#eef7fb] px-3 py-2.5 text-xs leading-relaxed text-gray-700">
                  Un avis d'enseignant n'est jamais montré à la famille. La vie scolaire le lit et décide s'il mérite
                  un incident, qu'elle rédige elle-même.
                  {peutOuvrirAvis && (
                    <>
                      {' '}
                      <Link to="/administration/disciplinaire?onglet=avis" className="font-semibold text-[#246990] hover:underline">
                        Ouvrir les avis dans la vie scolaire
                      </Link>
                    </>
                  )}
                </p>
              </>
            )
            )}
          </TabsContent>
        )}
        </Tabs>
      </div>

      {toast && (
        <ToastPortal>
          {shouldUseLongToast(toast.message, toast.type, toast) ? (
            <LongToast show message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          ) : (
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          )}
        </ToastPortal>
      )}
    </div>
  );
};

export default PageEnseignantSuivi;
